import { Checkbox, Divider, Icon, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import { ArrowDownIcon } from 'assets/icons';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import BasicPickerFormField from 'components/foundation/form-fields/BasicPickerFormField/BasicPickerFormField';
import { IBasicPickerValue } from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import { usePhotoVerificationPpeEnabled } from 'containers/photo-verification-report/hooks/usePhotoVerificationPpeFeatureFlags';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { t } from 'i18next';
import { cloneDeep, remove } from 'lodash';
import { useState } from 'react';
import {
  convertPhotoVerificationSettingsToString,
  IPhotoVerificationSettings,
  PositionPhotoVerificationSettingsActionType,
} from 'utils/positionUtils';
import {
  getPhotoVerificationSettingsFormActionKey,
  getPhotoVerificationSettingsFormActionTitle,
  getPhotoVerificationSettingsFormScanKey,
  getPhotoVerificationSettingsFormScanTitle,
  PhotoVerificationOptionType,
  photoVerificationSettingsFormAllActions,
  photoVerificationSettingsFormScans,
  useIsPPEScanTypeEnabled,
} from '../util/photoVerificationSettingsFormUtils';
import './PhotoVerificationSettingsFormFields.scss';

export interface IPhotoVerificationSettingsFormFieldsProps<T extends IPhotoVerificationSettingsFormFieldsData> {
  form: IFormRender<T>;
}

export interface IPhotoVerificationSettingsFormFieldsData {
  photoVerificationSettings: string;
  photoVerificationOptionType: PhotoVerificationOptionType;
}

function PhotoVerificationSettingsFormFields<T extends IPhotoVerificationSettingsFormFieldsData>({
  form,
}: IPhotoVerificationSettingsFormFieldsProps<T>) {
  const [settings, setSettings] = useState<IPhotoVerificationSettings>(
    JSON.parse(form.state.data.photoVerificationSettings)
  );

  const basicPickerOptions: Array<IBasicPickerValue<PhotoVerificationOptionType>> = [
    { id: PhotoVerificationOptionType.KIOSK_ONLY, name: t('Kiosk Only') },
    { id: PhotoVerificationOptionType.POSITION, name: t('Position') },
  ];

  const isPPEEnabled = usePhotoVerificationPpeEnabled();
  const isPPEScanTypeEnabled = useIsPPEScanTypeEnabled();

  function isPPEScansVisible(): boolean {
    return isPPEEnabled && (settings.ppe?.some((ppe) => ppe.enabled) ?? false);
  }

  return (
    <>
      <HeaderText title={t('Require Photo Verification')} className="my-4">
        <span>
          {t(
            `Select if photo verification should only be required when using a Kiosk that requires it, or if the position should always photo verify when performing certain clock actions (Position settings will override any Kiosk settings).`
          )}
        </span>
      </HeaderText>
      <BasicPickerFormField
        name="photoVerificationOptionType"
        data={basicPickerOptions}
        form={form}
        closeButtonRender={() => (
          <span className="drop-icon">
            <Icon svg={ArrowDownIcon} />
          </span>
        )}
        onChange={(value) => {
          setSettings({ ...settings, enabled: value === PhotoVerificationOptionType.POSITION });
          form.state.data.photoVerificationSettings = convertPhotoVerificationSettingsToString(settings);
        }}
      />

      {settings.enabled && (
        <>
          <DashboardRibbon theme={Theme.LIGHT} className="my-6">
            <div className="photo-verification-settings-form-fields-action-grid mx-4">
              <div className="row header">
                <h4>{t('When To Scan')}</h4>
                {isPPEEnabled && <h4>{t('Enable PPE Scan')}</h4>}
              </div>
              {photoVerificationSettingsFormAllActions.map((action) => {
                return (
                  <>
                    <Divider />
                    <Divider />
                    <div key={getPhotoVerificationSettingsFormActionKey(action)} className="row">
                      <Checkbox
                        key={getPhotoVerificationSettingsFormActionKey(action)}
                        checked={settings.actions.includes(action) === true}
                        label={getPhotoVerificationSettingsFormActionTitle(action)}
                        onChange={(checked, value) => {
                          const clonedSettings = cloneDeep(settings);
                          switch (action) {
                            case PositionPhotoVerificationSettingsActionType.CLOCK_IN:
                              remove(
                                clonedSettings.actions,
                                (a) => a === PositionPhotoVerificationSettingsActionType.CLOCK_IN
                              );
                              if (checked === true) {
                                clonedSettings.actions.push(PositionPhotoVerificationSettingsActionType.CLOCK_IN);
                              }
                              setSettings({ ...clonedSettings });
                              form.state.data.photoVerificationSettings =
                                convertPhotoVerificationSettingsToString(clonedSettings);
                              break;
                            case PositionPhotoVerificationSettingsActionType.CLOCK_OUT:
                              remove(
                                clonedSettings.actions,
                                (a) => a === PositionPhotoVerificationSettingsActionType.CLOCK_OUT
                              );
                              if (checked === true) {
                                clonedSettings.actions.push(PositionPhotoVerificationSettingsActionType.CLOCK_OUT);
                              }
                              setSettings({ ...clonedSettings });
                              form.state.data.photoVerificationSettings =
                                convertPhotoVerificationSettingsToString(clonedSettings);
                              break;
                            case PositionPhotoVerificationSettingsActionType.START_BREAK:
                              remove(
                                clonedSettings.actions,
                                (a) => a === PositionPhotoVerificationSettingsActionType.START_BREAK
                              );
                              if (checked === true) {
                                clonedSettings.actions.push(PositionPhotoVerificationSettingsActionType.START_BREAK);
                              }
                              setSettings({ ...clonedSettings });
                              form.state.data.photoVerificationSettings =
                                convertPhotoVerificationSettingsToString(clonedSettings);
                              break;
                            case PositionPhotoVerificationSettingsActionType.END_BREAK:
                              remove(
                                clonedSettings.actions,
                                (a) => a === PositionPhotoVerificationSettingsActionType.END_BREAK
                              );
                              if (checked === true) {
                                clonedSettings.actions.push(PositionPhotoVerificationSettingsActionType.END_BREAK);
                              }
                              setSettings({ ...clonedSettings });
                              form.state.data.photoVerificationSettings =
                                convertPhotoVerificationSettingsToString(clonedSettings);
                              break;
                            case PositionPhotoVerificationSettingsActionType.SWITCH:
                              remove(
                                clonedSettings.actions,
                                (a) => a === PositionPhotoVerificationSettingsActionType.SWITCH
                              );
                              if (checked === true) {
                                clonedSettings.actions.push(PositionPhotoVerificationSettingsActionType.SWITCH);
                              }
                              setSettings({ ...clonedSettings });
                              form.state.data.photoVerificationSettings =
                                convertPhotoVerificationSettingsToString(clonedSettings);
                              break;
                          }
                        }}
                      />
                      {isPPEEnabled && (
                        <Checkbox
                          key={getPhotoVerificationSettingsFormActionKey(action) + 'PPE'}
                          disabled={!settings.actions.includes(action)}
                          checked={settings.ppe?.find((ppe) => ppe.type === action)?.enabled ?? false}
                          onChange={(checked, value) => {
                            const clonedSettings = cloneDeep(settings);
                            let ppe = clonedSettings.ppe?.find((ppe) => ppe.type === action);
                            ppe = {
                              enabled: checked,
                              type: action,
                              scans: ppe?.scans ?? [],
                            };

                            clonedSettings.ppe = clonedSettings.ppe?.map((p) => {
                              if (ppe && p.type === action) {
                                return ppe;
                              }
                              return p;
                            });

                            setSettings({ ...clonedSettings });
                            form.state.data.photoVerificationSettings =
                              convertPhotoVerificationSettingsToString(clonedSettings);
                          }}
                        />
                      )}
                    </div>
                  </>
                );
              })}
            </div>
            {isPPEScansVisible() && (
              <div className="mx-4 mt-6">
                <HeaderText title={t('PPE Scan Items')}>
                  <span>{t(`Select the PPE items you wish to scan for.`)}</span>
                </HeaderText>
                <div className="mt-2 photo-verification-settings-form-fields">
                  {photoVerificationSettingsFormScans
                    .filter((scan) => isPPEScanTypeEnabled(scan))
                    .map((scan) => {
                      return (
                        <Checkbox
                          key={getPhotoVerificationSettingsFormScanKey(scan)}
                          label={getPhotoVerificationSettingsFormScanTitle(scan)}
                          checked={settings.ppe?.find((ppe) => ppe.enabled)?.scans.includes(scan) ?? false}
                          onChange={(checked, value) => {
                            const clonedSettings = cloneDeep(settings);
                            clonedSettings.ppe = clonedSettings.ppe?.map((ppe) => {
                              if (checked) {
                                ppe.scans.push(scan);
                              } else {
                                remove(ppe.scans, (s) => s === scan);
                              }
                              return ppe;
                            });
                            setSettings({ ...clonedSettings });
                            form.state.data.photoVerificationSettings =
                              convertPhotoVerificationSettingsToString(clonedSettings);
                          }}
                        />
                      );
                    })}
                </div>
              </div>
            )}
          </DashboardRibbon>
        </>
      )}
    </>
  );
}

export default PhotoVerificationSettingsFormFields;
